import { capitalize } from 'lodash-es';

import type { Locale } from '@change-corgi/config/locales';
import { getLocales } from '@change-corgi/config/locales';

import { acronymDictionary } from './acronymDictionary';

/**
 * Topic slugs may carry a locale suffix (e.g. `裏金-ja-jp`) indicating the language the
 * topic page is meant to be presented in. Returns the supported `Locale` encoded in the
 * slug, or `undefined` when there is no valid locale suffix.
 *
 * Matching against the canonical locale list (rather than a `xx-XX` regex) keeps it in
 * sync with the supported locales and handles non-standard region subtags like `es-419`.
 */
export function extractTopicLocaleFromSlug(slug: string): Locale | undefined {
	const lowerSlug = slug.toLowerCase();
	return getLocales().find((locale) => lowerSlug.endsWith(`-${locale.toLowerCase()}`));
}

/**
 * Topics in Local and Topic pages handle capitalization differently. This utility
 * helps to standardize the capitalization of topic names, handling some minor words and
 * acronyms appropriately.
 */
export function capitalizeTopic(topic: string): string {
	if (!topic) return '';

	const wordsToIgnore = ['and', 'or', 'so', 'a', 'an', 'the', 'in', 'on', 'at', 'by', 'for', 'of', 'to'];

	return topic
		.toLowerCase()
		.split(' ')
		.map((word, index, array) => {
			// Always capitalize first and last word
			if (index === 0 || index === array.length - 1) {
				// Handle known acronyms at beginning or end
				if (acronymDictionary.includes(word)) {
					return word.toUpperCase();
				}
				return capitalize(word);
			}

			// Handle minor words
			if (wordsToIgnore.includes(word)) {
				return word;
			}

			// Handle known acronyms
			if (acronymDictionary.includes(word)) {
				return word.toUpperCase();
			}

			return capitalize(word);
		})
		.join(' ');
}
